import { useState } from "react";

import { ArrowRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useCan } from "@/lib/auth/useCan";

import {
  useApproveRequest,
  useCancelRequest,
  useRejectRequest,
  useRequests,
  type RelationshipRequest,
} from "../api";
import { VerifyButton } from "./VerifyButton";

function actionLine(r: RelationshipRequest) {
  if (r.action === "add") {
    return (
      <>
        Link <b>{r.child_name}</b> under <b>{r.parent_name}</b>
      </>
    );
  }
  if (r.action === "remove") {
    return (
      <>
        Unlink <b>{r.child_name}</b> from <b>{r.parent_name}</b>
      </>
    );
  }
  return (
    <>
      Move <b>{r.child_name}</b>: {r.parent_name}
      <ArrowRight className="mx-1 inline h-3 w-3" />
      {r.new_parent_name}
    </>
  );
}

/**
 * The approval queue. Pending requests first with approve/reject (second-user
 * rule enforced server-side; the buttons only render when the backend says
 * this caller can review), then the decided history. Approved-but-unapplied
 * requests carry the Cinch-admin deep link + a verify button, because Cinch
 * has no API for these writes.
 */
export function RequestsPanel({ companyId }: { companyId?: string }) {
  const q = useRequests("all", companyId);
  const canWrite = useCan("parent_companies.write");

  if (q.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading change requests…</p>;
  }
  if (q.error) {
    return <p className="text-sm text-destructive">{String(q.error)}</p>;
  }
  const items = q.data?.items ?? [];
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No relationship change requests{companyId ? " for this company" : ""} yet.
      </p>
    );
  }
  const pending = items.filter((r) => r.status === "pending");
  const awaitingApply = items.filter(
    (r) => r.status === "approved" && !r.applied_at,
  );
  const decided = items.filter(
    (r) => r.status !== "pending" && !(r.status === "approved" && !r.applied_at),
  );

  return (
    <div className="flex flex-col gap-4">
      {pending.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Awaiting approval</h3>
          {pending.map((r) => (
            <RequestCard key={r.id} request={r} canWrite={canWrite} />
          ))}
        </section>
      )}
      {awaitingApply.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Approved — waiting on Cinch admin</h3>
          {awaitingApply.map((r) => (
            <RequestCard key={r.id} request={r} canWrite={canWrite} />
          ))}
        </section>
      )}
      {decided.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">History</h3>
          {decided.map((r) => (
            <RequestCard key={r.id} request={r} canWrite={canWrite} compact />
          ))}
        </section>
      )}
    </div>
  );
}

function RequestCard({
  request: r,
  canWrite,
  compact = false,
}: {
  request: RelationshipRequest;
  canWrite: boolean;
  compact?: boolean;
}) {
  const approve = useApproveRequest();
  const reject = useRejectRequest();
  const cancel = useCancelRequest();
  // can_review (server truth) gates the buttons; this only picks the right
  // explanation when they're absent — "you proposed it" vs "you lack the perm".
  const canAdmin = useCan("parent_companies.admin");
  const [note, setNote] = useState("");
  const busy = approve.isPending || reject.isPending || cancel.isPending;

  const statusLabel =
    r.status === "approved" ? (r.applied_at ? "Applied" : "Approved") : undefined;

  return (
    <Card>
      <CardContent className="flex flex-col gap-2 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge value={r.status} label={statusLabel} />
          <span className="text-sm">{actionLine(r)}</span>
          <span className="ml-auto text-xs text-muted-foreground">
            {new Date(r.created_at).toLocaleString()}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          Proposed by {r.requested_by_email ?? "unknown"} — “{r.reason}”
        </div>
        {r.reviewed_by_email && (
          <div className="text-xs text-muted-foreground">
            {r.status === "rejected" ? "Rejected" : "Approved"} by {r.reviewed_by_email}
            {r.reviewed_at ? ` · ${new Date(r.reviewed_at).toLocaleString()}` : ""}
            {r.review_note ? ` — “${r.review_note}”` : ""}
          </div>
        )}
        {r.applied_at && (
          <div className="text-xs text-muted-foreground">
            Verified live in Cinch · {new Date(r.applied_at).toLocaleString()}
          </div>
        )}

        {/* Approved but not yet applied: Cinch has no write API for these
            links, so a Cinch admin applies it via the deep link, then anyone
            verifies. */}
        {r.status === "approved" && !r.applied_at && !compact && (
          <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/60 p-2 text-xs">
            <span>
              Apply in the Cinch admin
              {r.action === "move" ? " (unlink the old parent, then add the new one)" : ""}
              , then verify:
            </span>
            {r.admin_link && (
              <a
                href={r.admin_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
              >
                Open Cinch admin <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <VerifyButton requestId={r.id} />
            {r.apply_error && <span className="text-warning">{r.apply_error}</span>}
          </div>
        )}

        {r.status === "pending" && !compact && (
          <div className="flex flex-col gap-2">
            {r.can_review && (
              <Textarea
                placeholder="Review note (optional)…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={1}
                className="text-sm"
              />
            )}
            <div className="flex flex-wrap items-center gap-2">
              {r.can_review ? (
                <>
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      approve.mutate(
                        { id: r.id, note: note || undefined },
                        {
                          onSuccess: () =>
                            toast.success("Approved — apply it in the Cinch admin next"),
                          onError: (e) => toast.error(e.message),
                        },
                      )
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      reject.mutate(
                        { id: r.id, note: note || undefined },
                        {
                          onSuccess: () => toast.success("Request rejected"),
                          onError: (e) => toast.error(e.message),
                        },
                      )
                    }
                  >
                    Reject
                  </Button>
                </>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {canAdmin
                    ? "You proposed this change — a different user with approval rights must sign off."
                    : `Approving needs the parent_companies.admin permission — ask an admin to grant it${r.requested_by_email ? ` (proposed by ${r.requested_by_email})` : ""}.`}
                </span>
              )}
              {canWrite && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  className="ml-auto text-muted-foreground"
                  onClick={() =>
                    cancel.mutate(r.id, {
                      onSuccess: () => toast.success("Request withdrawn"),
                      onError: (e) => toast.error(e.message),
                    })
                  }
                >
                  Withdraw
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
